<template>
  <div id="adset-budget-tab">
    <vue-tabs ref="adTabsContainer" @input="handleTabInput">
      <v-tab v-for="(adset, index) in value.adsets" :key="'adset-budgeting-' + index" :tabKey="'adset-budgeting-' + index" :hasError="value.adsets[index].hasError">
        <!-- Start title -->
        <div slot="title">
          <span class="align-middle pl-3 adset-title">
            {{ value.adsets[index].name == '' ? ('Ad Set ' + (index + 1)) : value.adsets[index].name }}
          </span>
        </div>
        <!-- End title -->

        <!-- Start content -->
        <div class="mt-2">
          <BudgetContent
            v-model="value.adsets[index]"
            :advertisingChannel="advertisingChannel"
            :adsetIndex="index"
            :serverErrors="serverErrors"
            :objective="objective"
            :objective-id="objectiveId"
            :mainEvent="mainEvent"
            :type="type"
            :type-id="typeId"
            :industry="industry"
            :budgetType="budgetType"
            :editing="editing && value.adsets[index].id > 0"
            :startDate="startDate"
            :endDate="endDate"
            @fireReachEstimator="fireReachEstimator"
            @fireAdsetBidHint="fireAdsetBidHint"
          />
        </div>
        <!-- End content -->
      </v-tab>
    </vue-tabs>
  </div>
</template>

<script>
import { VueTabs, VTab } from '@/components/vue-nav-tabs/vue-nav-tabs.js'
import BudgetContent from '@/views/campaigns/components/steps/components/BudgetContent/BudgetContent.vue'

export default {
  data () {
    return {
      fireReachEstimator:(adsetIndex) => {
        if (adsetIndex !== this.$refs.adTabsContainer.activeTabIndex) {
          return
        }
        this.setReachEstimator(adsetIndex)
      }
    }
  },
  props: {
    value: {
      required: true,
      type: Object
    },
    advertisingChannel: {
      type: Object,
      required: true
    },
    serverErrors: {
      required: true
    },
    objective: {
      type: String,
      required: true
    },
    objectiveId: {
      type: Number,
      required: true
    },
    industry: {
      type: Number,
      required: true
    },
    mainEvent: {
      type: String | null,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    typeId: {
      type: Number,
      required: true
    },
    editing: {
      type: Boolean,
      default: false,
      required: false
    },
    budgetType: {
      type: String,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    changedAdsetCountries: {
      type: Object,
      required: true
    }
  },
  components: {
    VueTabs,
    VTab,
    BudgetContent
  },
  watch: {
    value: {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    serverErrors: {
      handler () {
        this.checkAdsetsErrors()
      },
      deep: true
    },
    errors: {
      handler () {
        this.checkAdsetsErrors()
      },
      deep: true
    },
    changedAdsetCountries: {
      handler (newAdsetData) {
        if (newAdsetData && newAdsetData.countries) {
          this.setReachEstimator(newAdsetData.adsetIndex)
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    checkAdsetsErrors () {
      const errors = []
      const adsetsHasErrors = []

      this.serverErrors.items.forEach(item => {
        errors.push(item.field)
      })

      this.errors.items.forEach(item => {
        errors.push(item.field)
      })

      // add the errors to adsets has errors
      this.value.adsets.forEach((adset, index) => {
        errors.forEach(e => {
          if (e.includes(`.adsets.${index}`)) {
            this.value.adsets[index].hasError = true
            adsetsHasErrors.push(adset.id)
          }
        })
      })

      // remove the errors from adsets that dose not has errors
      this.value.adsets.forEach((adset, index) => {
        if (!adsetsHasErrors.includes(adset.id)) {
          this.value.adsets[index].hasError = false
        }
      })
    },
    async setReachEstimator (adsIndex) {
      let dailyBudget = this.value.adsets[adsIndex].budget.budget_value

      if (this.endDate !== '') {
        const startDate = this.startDate === '' ? new Date() : new Date(this.startDate)
        const endDate = new Date(this.endDate)
        const diffDays =
          Math.ceil(Math.abs(endDate - startDate) / (1000 * 60 * 60 * 24)) + 1

        if (diffDays > 0) {
          dailyBudget = dailyBudget / diffDays
        }
      }
      const data = {
        daily_budget: dailyBudget,
        objective_id: this.objectiveId,
        countries: this.value.adsets[adsIndex].locations.countries
      }

      if (data.daily_budget <= 0) {
        return
      }

      const response = await window.axios
        .post('/campaigns/reach-estimate', data, { hideLoader: true })
        .catch((error) => {
          window.axios.sendHandledSentryException(error)
        })

      if (response.data.success) {
        this.$set(this.value.adsets[adsIndex], 'reach_estimates', {
          reach_range: response.data.payload.avg.reach_range,
          clicks_range: response.data.payload.avg.clicks_range,
          reach: response.data.payload.avg.reach,
          clicks: response.data.payload.avg.clicks
        })
      }
    },
    handleTabInput (tabTitle) {
      const tabIndex = Number(tabTitle.replace('adset-budgeting-', ''))
      this.$emit('setActiveAdsetIndex', tabIndex)
    },
    fireAdsetBidHint (data) {
      this.$set(this.value.adsets[data.adsetIndex], 'showAdsetBidHint', data.status)
    }
  },
  mounted () {
    if (this.editing && this.advertisingChannel.supports_reach_estimate) {
      const activeAdsetIndex = this.$refs.adTabsContainer.activeTabIndex
      this.setReachEstimator(activeAdsetIndex)
    }
  }
}
</script>

<style lang="scss">
  #adset-budget-tab {
    .tabs__link {
      padding-top: 15px;
      padding-bottom: 15px;
    }
  }
</style>
